import { z } from "zod";
// ─── Server-side pagination contract ─────────────────────────────────────────
// One wire shape for every paginated list endpoint, so the API helpers
// (packages/db `paginate`), the route query parsing and the web hooks/pager
// (apps/web `usePaginatedQuery` / <Pagination>) all speak the same language.
//
//   request:  ?page=<1-based>&pageSize=<n>[&sort=<key>&dir=asc|desc]
//   response: { items: T[], pageInfo: { page, pageSize, total, totalPages } }
//
// Offset (page-number) pagination rather than keyset: these lists are sorted by
// arbitrary user-chosen columns and show "X–Y of N" + page controls, which
// keyset can't provide. Audit logs keep their keyset cursor (append-only,
// always newest-first, "Load more" UX) — a deliberately different contract.
export const DEFAULT_PAGE_SIZE = 25;
export const MAX_PAGE_SIZE = 100;
export const PageQuery = z.object({
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().min(1).max(MAX_PAGE_SIZE).default(DEFAULT_PAGE_SIZE),
});
export const SortDirection = z.enum(["asc", "desc"]);
// `?sort=&dir=` for a list whose sortable columns are `keys`. Unknown keys are
// a 400 (z.enum), so every sort key reaching the DB layer is whitelisted.
export function sortQuery(keys, defaults) {
    return z.object({
        sort: z.enum(keys).default(defaults.sort),
        dir: SortDirection.default(defaults.dir),
    });
}
export const PageInfo = z.object({
    // The page actually served — clamped to the last page when the requested one
    // is past the end (e.g. the last row of the final page was just deleted), so
    // the client can resync its page control instead of rendering an empty page.
    page: z.number().int().min(1),
    pageSize: z.number().int().min(1),
    total: z.number().int().min(0),
    totalPages: z.number().int().min(1),
});
export function paginated(item) {
    return z.object({ items: z.array(item), pageInfo: PageInfo });
}
// Maps a page's rows while keeping its pageInfo — services use this to turn a
// DB page into a DTO page.
export function mapPage(page, fn) {
    return { items: page.items.map(fn), pageInfo: page.pageInfo };
}
// Comma-separated list param (`?x=a,b,c`) → string[]; an empty string is [].
export function csvParam(item) {
    return z.preprocess((val) => typeof val === "string"
        ? val
            .split(",")
            .map((s) => s.trim())
            .filter(Boolean)
        : val, z.array(item));
}
//# sourceMappingURL=pagination.js.map